import { ApiTokensCard } from "@/components/profile/api-tokens-card";
import { LanguageFormatCard } from "@/components/profile/language-format-card";
import { RadimWhatsappLinkCard } from "@/components/profile/radim-whatsapp-link-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingLabel } from "@/components/ui/loading-label";
import { toast } from "@/components/ui/use-toast";
import { DiscordLinkField } from "@/components/users/discord-link-field";
import { SecondaryEmails } from "@/components/users/secondary-emails";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/profile` — first phase 6 page. Mirrors the Next-side
 * `src/app/(app)/profile/page.tsx`.
 *
 * Mutations toast on success/failure (shared `<Toaster>` mounted
 * in the `_app` layout) and call `router.invalidate()` (the
 * TanStack-router equivalent of Next's `router.refresh()`) so the
 * connected/disconnected card swaps in right after the round-trip
 * completes.
 */

type ProfileData = {
  id: string;
  name: string | null;
  email: string;
  discordUsername: string | null;
  /** Set only when the handle resolved to a real Discord account. */
  discordUserId: string | null;
  /** Null when the user signs in through GitLab. */
  password: { set: boolean } | null;
};

export const Route = createFileRoute("/_app/profile")({
  loader: () => api.get<ProfileData>("/api/me/profile"),
  component: ProfilePage,
});

function ProfilePage() {
  const { t } = useTranslation();
  const profile = Route.useLoaderData();
  const { isCustomer } = useLoaderData({ from: "/_app" });

  if (isCustomer) {
    return (
      <div className="space-y-6">
        <ProfileHeading name={profile.name} email={profile.email} />
        <LanguageFormatCard />
        {profile.password && <PasswordCard isSet={profile.password.set} />}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProfileHeading name={profile.name} email={profile.email} />
      <LanguageFormatCard />

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("prof.meeting.title")}</CardTitle>
            <CardDescription>
              <Trans
                i18nKey="prof.meeting.description"
                components={{
                  link: <Link to="/suggestions/time-entries" className="underline" />,
                }}
              />
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <DiscordLinkField
              userId={profile.id}
              initialHandle={profile.discordUsername}
              initialLinked={Boolean(profile.discordUserId)}
            />
            <div className="border-t pt-4">
              <SecondaryEmails userId={profile.id} primaryEmail={profile.email} />
            </div>
          </CardContent>
        </Card>

        <RadimWhatsappLinkCard />
        <BrowserTimerCard />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("prof.tokens.title")}</CardTitle>
          <CardDescription>
            <Trans
              i18nKey="prof.tokens.description"
              components={{
                code: <code className="text-xs" />,
                link: <Link to="/suggestions/time-entries" className="underline" />,
              }}
            />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ApiTokensCard />
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileHeading({ name, email }: { name: string | null; email: string }) {
  const { t } = useTranslation();
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold">{t("prof.title")}</h1>
      <p className="text-sm text-muted-foreground mt-1">
        {name} · {email}
      </p>
    </div>
  );
}

const PASSWORD_MIN_LENGTH = 10;

function PasswordCard({ isSet }: { isSet: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const body = () => ({ password, ...(current ? { currentPassword: current } : {}) });
  const save = useApiMutation(() => api.put("/api/auth/password", body()), {
    onSuccess: () => {
      setCurrent("");
      setPassword("");
      setConfirm("");
      toast({ title: t("prof.password.saved"), variant: "success" });
      navigate({ to: "/dashboard" });
    },
  });
  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = password.length >= PASSWORD_MIN_LENGTH && password === confirm;
  const inputClass =
    "mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <Card className="max-w-lg" data-testid="password-card">
      <CardHeader>
        <CardTitle>{t("prof.password.title")}</CardTitle>
        <CardDescription>
          {isSet ? t("prof.password.descriptionSet") : t("prof.password.descriptionUnset")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !save.pending) save.mutate();
          }}
        >
          {isSet && (
            <label className="block">
              <span className="text-sm font-medium">{t("prof.password.current")}</span>
              <input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className={inputClass}
              />
              <span className="text-xs text-muted-foreground">
                {t("prof.password.currentHint")}
              </span>
            </label>
          )}
          <label className="block">
            <span className="text-sm font-medium">
              {isSet ? t("prof.password.newPassword") : t("prof.password.password")}
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
            <span className="text-xs text-muted-foreground">
              {t("prof.password.minLength", { count: PASSWORD_MIN_LENGTH })}
            </span>
          </label>
          <label className="block">
            <span className="text-sm font-medium">{t("prof.password.repeat")}</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
            />
            {mismatch && (
              <span className="text-xs text-destructive">{t("prof.password.mismatch")}</span>
            )}
          </label>
          {save.error && <p className="text-xs text-destructive">{save.error}</p>}
          <div>
            <Button type="submit" size="sm" disabled={!valid || save.pending}>
              {save.pending ? (
                <LoadingLabel text={t("prof.password.saving")} />
              ) : isSet ? (
                t("prof.password.change")
              ) : (
                t("prof.password.set")
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function BrowserTimerCard() {
  const { t } = useTranslation();
  return (
    <Card data-testid="browser-timer-card">
      <CardHeader>
        <CardTitle>{t("prof.timer.title")}</CardTitle>
        <CardDescription>{t("prof.timer.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Button asChild>
          <a href="/install/hub-timer-extension.zip" download>
            {t("prof.timer.download")}
          </a>
        </Button>
        <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
          <li>{t("prof.timer.step1")}</li>
          <li>
            <Trans i18nKey="prof.timer.step2" components={{ code: <code className="text-xs" /> }} />
          </li>
          <li>{t("prof.timer.step3")}</li>
          <li>{t("prof.timer.step4")}</li>
        </ol>
        <p className="text-muted-foreground">{t("prof.timer.update")}</p>
      </CardContent>
    </Card>
  );
}
